import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, Github, Eye, Shield, FileText, Lock, X } from 'lucide-react';

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [showConfidentialModal, setShowConfidentialModal] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');

  const categories = ['All', 'Cloud Architecture', 'AI/ML', 'Cybersecurity', 'Cloud Security', 'Blockchain', 'Intelligence', 'OSINT Tools', 'Security Tools', 'IoT/Hardware'];

  const projects = [
    {
      title: 'UltraCare',
      subtitle: 'Multi-Tenant Healthcare SaaS Backend',
      tech: ['TypeScript', 'Hono v4', 'Cloudflare Workers', 'Neon PostgreSQL', 'Drizzle ORM'],
      description: 'Personal production-grade backend using Hono v4 on Cloudflare Workers (edge serverless), Neon PostgreSQL + Drizzle ORM, Zod validation, JWT auth, Cloudflare KV rate limiting, Backblaze B2 storage, and Resend REST email. Features multi-tenant RBAC with strict data isolation and PII-scrubbed Sentry error monitoring.',
      date: 'Dec 2025',
      category: 'Cloud Architecture',
      githubUrl: null,
      liveUrl: null,
      hasPrivateRepo: true
    },
    {
      title: 'NeuroTrade',
      subtitle: 'AI-Integrated Automated Trading System',
      tech: ['Python', 'JavaScript', 'N8n', 'Gemini API'],
      description: 'Gemini LLM-powered agent performing real-time news sentiment analysis to generate signals and execute orders via Alpaca; projected CAGR of 6.21% with multi-channel alerting (Email + Telegram).',
      date: 'Nov 2025',
      category: 'AI/ML',
      githubUrl: null,
      liveUrl: null,
      hasPrivateRepo: true
    },
    {
      title: 'MasterC2',
      subtitle: 'A lightweight Command and Control Server',
      tech: ['AWS', 'Metasploit', 'Sliver', 'PenTester Framework', 'Cloud Security'],
      description: 'Built MasterC2, a lightweight C2 server on AWS Lightsail with 2GB RAM, 60GB storage, and 1Tbps network. Integrated Metasploit, PenTester Framework, and Sliver, enabling 2,650+ security modules for offensive operations.',
      status: 'Work in progress',
      date: 'Jun 2025',
      category: 'Cybersecurity',
      githubUrl: null,
      liveUrl: null,
      hasPrivateRepo: true
    },
    {
      title: 'CloudShield',
      subtitle: 'DDoS Prevention on Cloud Architecture',
      tech: ['GCP', 'AI/ML', 'Cloud Architecture', 'DDoS Protection', 'Auto-scaling'],
      description: 'Designed CloudShield, a DDoS protection system with AI-driven detection, auto-scaling defenses, and real-time filtering. Engineered for low-latency threat mitigation and seamless cloud deployment.',
      date: 'Mar 2025',
      category: 'Cloud Security',
      githubUrl: null,
      liveUrl: null,
      hasPrivateRepo: true
    },
    {
      title: 'IntrudeTransaction',
      subtitle: 'Gasless Transaction on Ganache Blockchain',
      tech: ['Blockchain', 'NodeJS', 'Ganache', 'Meta-transactions', 'Ethereum'],
      description: 'Designed IntrudeTransaction, enabling gasless transactions on testnet via meta-transactions. Used a designated relayer for gas fees, ensuring secure, signature-based validation across 20+ successful transactions.',
      date: 'Jan 2025',
      category: 'Blockchain',
      githubUrl: 'https://github.com/hm05/IntrudeTransaction',
      liveUrl: null,
      hasPublicRepo: true
    },
    {
      title: 'DARC',
      subtitle: 'Dark Web Monitoring and Analysis',
      tech: ['Python', 'Scrapy', 'Tor Network', 'Data Analysis', 'OSINT'],
      description: 'Developed a dark web intelligence system using Scrapy and the Tor network to crawl and analyze .onion sites. Extracted actionable insights on illicit activities to support cybersecurity operations. This project was developed during the Gurugram Police Cyber Security Summer Internship (GPCSSI 2024).',
      date: 'Jun 2024',
      category: 'Intelligence',
      githubUrl: null,
      liveUrl: null,
      isConfidential: true,
      internshipProject: true,
      organization: 'Gurugram Police',
      hasEngagementLetter: true,
      engagementLetterUrl: 'https://drive.google.com/file/d/1mj6ePlmf0HApbXbkzEFlVT_ld0HjYxpb/view?usp=drive_link'
    },
    {
      title: 'DorkGen',
      subtitle: 'Automated Google Dork Generator',
      tech: ['Python', 'Web Scraping', 'OSINT', 'Search Automation'],
      description: 'Built an automated tool to generate custom Google dorks with filters for site, filetype, and keywords. Enhanced search efficiency and supported deep investigative tasks, including username-based queries. This project was developed during the Gurugram Police Cyber Security Summer Internship (GPCSSI 2024).',
      date: 'Jun 2024',
      category: 'OSINT Tools',
      githubUrl: null,
      liveUrl: null,
      isConfidential: true,
      internshipProject: true,
      organization: 'Gurugram Police',
      hasEngagementLetter: true,
      engagementLetterUrl: 'https://drive.google.com/file/d/1mj6ePlmf0HApbXbkzEFlVT_ld0HjYxpb/view?usp=drive_link'
    },
    {
      title: 'FuzzGuard',
      subtitle: 'Web Application Security Scanning Tool',
      tech: ['Python', 'Security Testing', 'Fuzzing', 'Brute Force', 'SMB Enumeration'],
      description: 'Built FuzzGuard, a security tool for HTTP fuzzing, subdomain fuzzing, directory fuzzing, SSH, FTP, MySQL brute force, and SMB enumeration. Tested on 25+ systems, improving penetration testing efficiency.',
      date: 'Jun 2023',
      category: 'Security Tools',
      githubUrl: 'https://github.com/hm05/FuzzGuard',
      liveUrl: null,
      hasPublicRepo: true
    },
    {
      title: 'Arduino Bluetooth Controlled Car',
      subtitle: 'IoT Project with Wireless Control',
      tech: ['Arduino', 'Bluetooth', 'C++', 'GPIO', 'Embedded Systems'],
      description: 'Developed a Bluetooth-controlled car with a 10m wireless range, utilizing Arduino programming and GPIO modules for motor control and wireless communication. Gained hands-on experience in embedded systems, sensor integration, and real-time data transmission.',
      date: 'Dec 2022',
      category: 'IoT/Hardware',
      githubUrl: 'https://github.com/hm05/Arduino-Car',
      liveUrl: null,
      hasPublicRepo: true
    }
  ];

  const filteredProjects = activeCategory === 'All' 
    ? projects 
    : projects.filter(p => p.category === activeCategory);

  const handleProjectClick = (project) => {
    if (project.isConfidential) {
      setShowConfidentialModal(project);
    } else {
      setSelectedProject(project);
    }
  };

  return (
    <section id="projects" className="py-24 bg-white dark:bg-black transition-colors duration-300">
      <div className="container mx-auto px-6 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 dark:text-white mb-4 font-inter">
            Projects
          </h2>
          <p className="text-lg text-gray-500 dark:text-gray-400 font-inter">Featured work in Backend, Security, and Cloud.</p>
        </motion.div>

        {/* Categories */}
        <div className="flex flex-wrap justify-center gap-2 mb-16">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeCategory === cat 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <motion.div 
          layout
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          <AnimatePresence mode="popLayout">
            {filteredProjects.map((project, index) => (
              <motion.div
                layout
                key={project.title}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="apple-card p-8 group cursor-pointer flex flex-col h-full"
                onClick={() => handleProjectClick(project)}
              >
                <div className="flex justify-between items-start mb-6">
                  <div className={`p-3 rounded-2xl bg-gray-50 dark:bg-gray-800 text-blue-600 dark:text-blue-400`}>
                    {project.isConfidential ? <Lock size={24} /> : <Shield size={24} />}
                  </div>
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">{project.date}</span>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                  {project.title}
                </h3>
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-6 flex-grow">{project.subtitle}</p>

                <div className="flex flex-wrap gap-2 mb-6">
                  {project.tech.slice(0, 3).map((t) => (
                    <span key={t} className="text-[10px] font-bold px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      {t}
                    </span>
                  ))}
                  {project.tech.length > 3 && (
                    <span className="text-[10px] font-bold px-2 py-1 rounded bg-gray-100 dark:bg-gray-800 text-gray-400 uppercase tracking-wider">
                      +{project.tech.length - 3}
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-between pt-6 border-t border-gray-100 dark:border-gray-800">
                  <span className="text-xs font-medium text-blue-600 dark:text-blue-400 flex items-center gap-1">
                    Learn more <Eye size={14} />
                  </span>
                  <div className="flex gap-3">
                    {project.githubUrl && <Github size={18} className="text-gray-400 hover:text-black dark:hover:text-white" />}
                    {project.liveUrl && <ExternalLink size={18} className="text-gray-400 hover:text-black dark:hover:text-white" />}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Regular Project Modal */}
        <AnimatePresence>
          {selectedProject && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/40 backdrop-blur-md"
                onClick={() => setSelectedProject(null)}
              />
              <motion.div 
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="relative bg-white dark:bg-[#1c1c1e] w-full max-w-2xl rounded-[32px] p-10 shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
              >
                <button 
                  onClick={() => setSelectedProject(null)}
                  className="absolute top-6 right-6 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 transition-colors"
                >
                  <X size={20} />
                </button>

                <div className="mb-8">
                  <span className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-[0.2em] mb-4 block">
                    {selectedProject.category}
                  </span>
                  <h3 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">{selectedProject.title}</h3>
                  <p className="text-xl text-gray-500 dark:text-gray-400">{selectedProject.subtitle}</p>
                </div>

                <div className="prose dark:prose-invert max-w-none mb-10">
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-lg">
                    {selectedProject.description}
                  </p>
                </div>

                <div className="space-y-8">
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-4">Tech Stack</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedProject.tech.map((t) => (
                        <span key={t} className="px-4 py-2 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium border border-gray-100 dark:border-gray-700">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-4 pt-8 border-t border-gray-100 dark:border-gray-800">
                    {selectedProject.githubUrl && (
                      <a 
                        href={selectedProject.githubUrl} 
                        target="_blank" 
                        rel="noreferrer"
                        className="btn-primary px-8 py-4 flex items-center gap-2"
                      >
                        <Github size={20} /> View Source
                      </a>
                    )}
                    {selectedProject.liveUrl && (
                      <a 
                        href={selectedProject.liveUrl} 
                        target="_blank" 
                        rel="noreferrer"
                        className="btn-secondary px-8 py-4 flex items-center gap-2"
                      >
                        <ExternalLink size={20} /> Live Demo
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Confidential Modal */}
        <AnimatePresence>
          {showConfidentialModal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/40 backdrop-blur-md"
                onClick={() => setShowConfidentialModal(null)}
              />
              <motion.div 
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="relative bg-white dark:bg-[#1c1c1e] w-full max-w-2xl rounded-[32px] p-10 shadow-2xl border border-red-100 dark:border-red-900/30"
              >
                <div className="mb-8">
                  <div className="w-12 h-12 rounded-2xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center text-red-600 mb-6">
                    <Shield size={28} />
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Confidential Project</h3>
                  <p className="text-red-600 dark:text-red-400 font-medium mb-4">{showConfidentialModal.organization} Internship</p>
                  
                  <div className="bg-red-50 dark:bg-red-900/10 p-6 rounded-2xl border border-red-100 dark:border-red-900/20 mb-8 text-sm leading-relaxed text-red-800 dark:text-red-200">
                    <p className="flex gap-3">
                      <Lock size={18} className="shrink-0 mt-0.5" />
                      Due to the sensitive nature of law enforcement operations, the source code and implementation details for <strong>{showConfidentialModal.title}</strong> are confidential.
                    </p>
                  </div>

                  <h4 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-4">Tech Stack Involved</h4>
                  <div className="flex flex-wrap gap-2">
                    {showConfidentialModal.tech.map((t) => (
                      <span key={t} className="px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 text-xs font-medium">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex gap-4">
                  {showConfidentialModal.hasEngagementLetter && (
                    <a 
                      href={showConfidentialModal.engagementLetterUrl} 
                      target="_blank" 
                      rel="noreferrer"
                      className="btn-primary px-6 py-3 flex items-center gap-2 text-sm"
                    >
                      <FileText size={18} /> Engagement Letter
                    </a>
                  )}
                  <button 
                    onClick={() => setShowConfidentialModal(null)}
                    className="btn-secondary px-6 py-3 text-sm"
                  >
                    Close
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Projects;

